import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import AIKeyRequiredDialog from '../components/AIKeyRequiredDialog';
import {
  Plus, Search, Building2, Lightbulb, ExternalLink, Instagram, Globe,
  Facebook, Linkedin, Star, MoreVertical, Trash2, Edit, CheckCircle,
  Clock, XCircle, ArrowRight, Users, ChevronRight, Eye, Sparkles, Loader2, Wand2
} from 'lucide-react';

const ResearchPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [inboxIdeas, setInboxIdeas] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [activeTab, setActiveTab] = useState('inbox');
  const [aiAnalyzing, setAiAnalyzing] = useState(null); // company id being analyzed
  const [showAIKeyDialog, setShowAIKeyDialog] = useState(false);

  // Dialog states
  const [companyDialog, setCompanyDialog] = useState(false);
  const [ideaDialog, setIdeaDialog] = useState(false);
  const [taskDialog, setTaskDialog] = useState(false);
  const [inboxIdeaDialog, setInboxIdeaDialog] = useState(false);
  const [inboxTaskDialog, setInboxTaskDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingIdea, setEditingIdea] = useState(null);
  const [editingInboxIdea, setEditingInboxIdea] = useState(null);
  const [selectedIdeaForTask, setSelectedIdeaForTask] = useState(null);
  const [selectedInboxIdeaForTask, setSelectedInboxIdeaForTask] = useState(null);

  // Form states
  const [companyForm, setCompanyForm] = useState({
    name: '', description: '', website: '', instagram: '', facebook: '',
    linkedin: '', google_reviews: '', tiktok: '', notes: '', category: 'concurrent'
  });

  const [ideaForm, setIdeaForm] = useState({
    company_id: '', title: '', description: '', category: 'algemeen',
    source: '', priority: 'normaal', image_url: ''
  });

  const [taskForm, setTaskForm] = useState({
    assigned_to: '', due_date: '', priority: 'normaal'
  });

  const [inboxIdeaForm, setInboxIdeaForm] = useState({
    title: '', description: '', category: 'algemeen', priority: 'normaal'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companiesRes, ideasRes, employeesRes, statsRes, inboxRes] = await Promise.all([
        api.get('/research/companies'),
        api.get('/research/ideas'),
        api.get('/employees'),
        api.get('/research/stats'),
        api.get('/ideas-inbox')
      ]);
      setCompanies(companiesRes.data);
      setIdeas(ideasRes.data);
      setEmployees(employeesRes.data);
      setStats(statsRes.data);
      setInboxIdeas(inboxRes.data);
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await api.put(`/research/companies/${editingCompany.id}`, companyForm);
        toast.success('Bedrijf bijgewerkt');
      } else {
        await api.post('/research/companies', companyForm);
        toast.success('Bedrijf toegevoegd');
      }
      setCompanyDialog(false);
      resetCompanyForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm('Bedrijf en bijbehorende ideeën verwijderen?')) return;
    try {
      await api.delete(`/research/companies/${id}`);
      toast.success('Bedrijf verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleSaveIdea = async (e) => {
    e.preventDefault();
    try {
      if (editingIdea) {
        await api.put(`/research/ideas/${editingIdea.id}`, ideaForm);
        toast.success('Idee bijgewerkt');
      } else {
        await api.post('/research/ideas', ideaForm);
        toast.success('Idee toegevoegd');
      }
      setIdeaDialog(false);
      resetIdeaForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleDeleteIdea = async (id) => {
    if (!window.confirm('Idee verwijderen?')) return;
    try {
      await api.delete(`/research/ideas/${id}`);
      toast.success('Idee verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleUpdateIdeaStatus = async (ideaId, status) => {
    try {
      await api.put(`/research/ideas/${ideaId}/status?status=${status}`);
      toast.success('Status bijgewerkt');
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken status');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/research/ideas/${selectedIdeaForTask.id}/create-task`, null, {
        params: {
          assigned_to: taskForm.assigned_to || null,
          due_date: taskForm.due_date || null,
          priority: taskForm.priority
        }
      });
      toast.success('Taak aangemaakt en gekoppeld aan Doelen & Structuur');
      setTaskDialog(false);
      setSelectedIdeaForTask(null);
      setTaskForm({ assigned_to: '', due_date: '', priority: 'normaal' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij aanmaken taak');
    }
  };

  // Inbox Ideas Handlers
  const handleSaveInboxIdea = async (e) => {
    e.preventDefault();
    try {
      if (editingInboxIdea) {
        await api.put(`/ideas-inbox/${editingInboxIdea.id}`, inboxIdeaForm);
        toast.success('Idee bijgewerkt');
      } else {
        await api.post('/ideas-inbox', inboxIdeaForm);
        toast.success('Idee toegevoegd aan inbox');
      }
      setInboxIdeaDialog(false);
      resetInboxIdeaForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleDeleteInboxIdea = async (id) => {
    if (!window.confirm('Idee verwijderen?')) return;
    try {
      await api.delete(`/ideas-inbox/${id}`);
      toast.success('Idee verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleUpdateInboxIdeaStatus = async (ideaId, status) => {
    try {
      await api.put(`/ideas-inbox/${ideaId}/status?status=${status}`);
      toast.success('Status bijgewerkt');
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken status');
    }
  };

  const handleConvertInboxIdeaToTask = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/ideas-inbox/${selectedInboxIdeaForTask.id}/convert-to-action`, null, {
        params: {
          assigned_to: taskForm.assigned_to || null,
          due_date: taskForm.due_date || null,
          priority: taskForm.priority
        }
      });
      toast.success('Actie aangemaakt en gekoppeld aan Doelen & Structuur');
      setInboxTaskDialog(false);
      setSelectedInboxIdeaForTask(null);
      setTaskForm({ assigned_to: '', due_date: '', priority: 'normaal' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij aanmaken actie');
    }
  };

  const resetInboxIdeaForm = () => {
    setInboxIdeaForm({ title: '', description: '', category: 'algemeen', priority: 'normaal' });
    setEditingInboxIdea(null);
  };

  const openEditInboxIdea = (idea) => {
    setEditingInboxIdea(idea);
    setInboxIdeaForm({
      title: idea.title,
      description: idea.description || '',
      category: idea.category || 'algemeen',
      priority: idea.priority || 'normaal'
    });
    setInboxIdeaDialog(true);
  };

  const openInboxTaskDialog = (idea) => {
    setSelectedInboxIdeaForTask(idea);
    setTaskForm({ assigned_to: '', due_date: '', priority: idea.priority || 'normaal' });
    setInboxTaskDialog(true);
  };

  const handleAiAnalyze = async (companyId) => {
    setAiAnalyzing(companyId);
    try {
      const res = await api.post(`/research/companies/${companyId}/ai-analyze`);
      const data = res.data;
      
      if (data.ideas_generated > 0) {
        toast.success(`${data.ideas_generated} ideeën gegenereerd door AI!`, {
          description: `Bronnen geanalyseerd: ${data.scraped_sources?.join(', ') || 'Beschrijving'}`
        });
      } else if (data.raw_response) {
        toast.info('AI analyse voltooid - bekijk de ruwe suggesties');
      } else {
        toast.warning('Geen ideeën gegenereerd. Voeg meer informatie toe aan het bedrijf.');
      }
      
      fetchData();
    } catch (error) {
      console.error('AI analysis error:', error);
      if (error.response?.status === 412 || error.response?.data?.detail === 'NO_API_KEY') {
        setShowAIKeyDialog(true);
      } else {
        toast.error('Fout bij AI analyse. Probeer het later opnieuw.');
      }
    } finally {
      setAiAnalyzing(null);
    }
  };

  const resetCompanyForm = () => {
    setCompanyForm({
      name: '', description: '', website: '', instagram: '', facebook: '',
      linkedin: '', google_reviews: '', tiktok: '', notes: '', category: 'concurrent'
    });
    setEditingCompany(null);
  };

  const resetIdeaForm = () => {
    setIdeaForm({
      company_id: '', title: '', description: '', category: 'algemeen',
      source: '', priority: 'normaal', image_url: ''
    });
    setEditingIdea(null);
  };

  const openEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name,
      description: company.description || '',
      website: company.website || '',
      instagram: company.instagram || '',
      facebook: company.facebook || '',
      linkedin: company.linkedin || '',
      google_reviews: company.google_reviews || '',
      tiktok: company.tiktok || '',
      notes: company.notes || '',
      category: company.category || 'concurrent'
    });
    setCompanyDialog(true);
  };

  const openAddIdea = (companyId) => {
    resetIdeaForm();
    setIdeaForm(prev => ({ ...prev, company_id: companyId }));
    setIdeaDialog(true);
  };

  const openEditIdea = (idea) => {
    setEditingIdea(idea);
    setIdeaForm({
      company_id: idea.company_id,
      title: idea.title,
      description: idea.description || '',
      category: idea.category || 'algemeen',
      source: idea.source || '',
      priority: idea.priority || 'normaal',
      image_url: idea.image_url || ''
    });
    setIdeaDialog(true);
  };

  const openTaskDialog = (idea) => {
    setSelectedIdeaForTask(idea);
    setTaskForm({ assigned_to: '', due_date: '', priority: idea.priority || 'normaal' });
    setTaskDialog(true);
  };

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCompanyIdeas = (companyId) => ideas.filter(i => i.company_id === companyId);

  const getStatusBadge = (status) => {
    const styles = {
      nieuw: 'bg-blue-100 text-blue-700',
      in_behandeling: 'bg-amber-100 text-amber-700',
      geimplementeerd: 'bg-emerald-100 text-emerald-700',
      afgewezen: 'bg-stone-100 text-stone-500'
    };
    const labels = {
      nieuw: 'Nieuw',
      in_behandeling: 'In behandeling',
      geimplementeerd: 'Geïmplementeerd',
      afgewezen: 'Afgewezen'
    };
    return <Badge className={styles[status] || styles.nieuw}>{labels[status] || status}</Badge>;
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      menu: 'Menu', marketing: 'Marketing', service: 'Service',
      interieur: 'Interieur', proces: 'Proces', personeel: 'Personeel', algemeen: 'Algemeen'
    };
    return labels[cat] || cat;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      laag: 'bg-stone-100 text-stone-600',
      normaal: 'bg-blue-100 text-blue-600',
      hoog: 'bg-rose-100 text-rose-600'
    };
    return <Badge variant="outline" className={styles[priority]}>{priority}</Badge>;
  };

  const SocialLink = ({ url, icon: Icon, label }) => {
    if (!url) return null;
    return (
      <a
        href={url.startsWith('http') ? url : `https://${url}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-sm text-[#57534E] hover:text-[#C2410C] transition-colors"
        title={label}
      >
        <Icon className="w-4 h-4" />
      </a>
    );
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#C2410C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#57534E]">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="research-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Research
          </h1>
          <p className="text-[#57534E] mt-1">
            Ideeën verzamelen en concurrenten analyseren
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inboxIdeas.length}</p>
                <p className="text-sm text-[#57534E]">Inbox</p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#C2410C]/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#C2410C]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_companies}</p>
                <p className="text-sm text-[#57534E]">Bedrijven</p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_ideas}</p>
                <p className="text-sm text-[#57534E]">Ideeën</p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.ideas_by_status?.in_behandeling || 0}</p>
                <p className="text-sm text-[#57534E]">In behandeling</p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.ideas_by_status?.geimplementeerd || 0}</p>
                <p className="text-sm text-[#57534E]">Geïmplementeerd</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="inbox" className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4" />
            Ideeën Inbox
            {inboxIdeas.filter(i => i.status === 'nieuw').length > 0 && (
              <Badge className="bg-cyan-100 text-cyan-700 ml-1">{inboxIdeas.filter(i => i.status === 'nieuw').length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="companies" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Bedrijven
          </TabsTrigger>
        </TabsList>

        {/* Inbox Tab */}
        <TabsContent value="inbox" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[#57534E]">Algemene ideeën en inspiratie - klik om naar actie om te zetten</p>
            <Button className="btn-primary" onClick={() => { resetInboxIdeaForm(); setInboxIdeaDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Idee toevoegen
            </Button>
          </div>

          {inboxIdeas.length === 0 ? (
            <Card className="card-base">
              <CardContent className="py-12 text-center">
                <Lightbulb className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-lg font-semibold">Nog geen ideeën in je inbox</p>
                <p className="text-[#57534E] mt-1">Voeg ideeën toe via het dashboard of de knop hierboven</p>
                <Button className="btn-primary mt-4" onClick={() => { resetInboxIdeaForm(); setInboxIdeaDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Eerste idee toevoegen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inboxIdeas.map(idea => (
                <Card 
                  key={idea.id} 
                  className="card-base cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openInboxTaskDialog(idea)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold line-clamp-2">{idea.title}</h3>
                        {idea.description && (
                          <p className="text-sm text-[#57534E] mt-1 line-clamp-2">{idea.description}</p>
                        )}
                      </div>
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditInboxIdea(idea)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => handleDeleteInboxIdea(idea.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant="outline" className="text-xs">{getCategoryLabel(idea.category)}</Badge>
                      {getPriorityBadge(idea.priority)}
                      {getStatusBadge(idea.status)}
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <span className="text-xs text-[#57534E]">
                        {new Date(idea.created_at).toLocaleDateString('nl-NL')}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-[#C2410C] hover:text-[#C2410C] hover:bg-[#C2410C]/10"
                        onClick={(e) => { e.stopPropagation(); openInboxTaskDialog(idea); }}
                      >
                        <ArrowRight className="w-4 h-4 mr-1" />
                        Naar actie
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Companies Tab */}
        <TabsContent value="companies" className="space-y-4">
          {/* Search */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#57534E]" />
              <Input
                placeholder="Zoek bedrijven..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button className="btn-primary" onClick={() => { resetCompanyForm(); setCompanyDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Bedrijf toevoegen
            </Button>
          </div>

          {/* Companies Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredCompanies.length === 0 ? (
          <Card className="card-base col-span-full">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-lg font-semibold">Nog geen bedrijven</p>
              <p className="text-[#57534E] mt-1">Voeg een bedrijf toe om te starten met research</p>
              <Button className="btn-primary mt-4" onClick={() => { resetCompanyForm(); setCompanyDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Eerste bedrijf toevoegen
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredCompanies.map(company => {
            const companyIdeas = getCompanyIdeas(company.id);
            return (
              <Card key={company.id} className="card-base overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#C2410C] to-[#EA580C] flex items-center justify-center text-white font-bold text-lg">
                        {company.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{company.name}</CardTitle>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {company.category === 'concurrent' ? 'Concurrent' : company.category === 'inspiratie' ? 'Inspiratie' : 'Leverancier'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleAiAnalyze(company.id)}
                        disabled={aiAnalyzing === company.id}
                        title="AI Analyse - Genereer ideeën"
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      >
                        {aiAnalyzing === company.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Wand2 className="w-4 h-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditCompany(company)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-rose-600" onClick={() => handleDeleteCompany(company.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {company.description && (
                    <p className="text-sm text-[#57534E] line-clamp-2">{company.description}</p>
                  )}

                  {/* Social Links */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <SocialLink url={company.website} icon={Globe} label="Website" />
                    <SocialLink url={company.instagram} icon={Instagram} label="Instagram" />
                    <SocialLink url={company.facebook} icon={Facebook} label="Facebook" />
                    <SocialLink url={company.linkedin} icon={Linkedin} label="LinkedIn" />
                    <SocialLink url={company.google_reviews} icon={Star} label="Google Reviews" />
                  </div>

                  {/* Ideas Section */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-amber-500" />
                        Ideeën ({companyIdeas.length})
                      </h4>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleAiAnalyze(company.id)}
                          disabled={aiAnalyzing === company.id}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        >
                          {aiAnalyzing === company.id ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Wand2 className="w-4 h-4 mr-1" />
                          )}
                          AI
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openAddIdea(company.id)}>
                          <Plus className="w-4 h-4 mr-1" />
                          Idee
                        </Button>
                      </div>
                    </div>

                    {companyIdeas.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-sm text-[#57534E] mb-3">Nog geen ideeën</p>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleAiAnalyze(company.id)}
                          disabled={aiAnalyzing === company.id}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        >
                          {aiAnalyzing === company.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Wand2 className="w-4 h-4 mr-2" />
                          )}
                          Genereer ideeën met AI
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {companyIdeas.slice(0, 5).map(idea => (
                          <div
                            key={idea.id}
                            className="p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{idea.title}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {getStatusBadge(idea.status)}
                                  <Badge variant="outline" className="text-xs">{getCategoryLabel(idea.category)}</Badge>
                                </div>
                              </div>
                              <div className="flex gap-1">
                                {idea.status !== 'geimplementeerd' && idea.status !== 'afgewezen' && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => openTaskDialog(idea)}
                                    title="Actie aanmaken"
                                  >
                                    <ArrowRight className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => openEditIdea(idea)}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-rose-600"
                                  onClick={() => handleDeleteIdea(idea.id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                        {companyIdeas.length > 5 && (
                          <p className="text-xs text-center text-[#57534E] pt-2">
                            +{companyIdeas.length - 5} meer ideeën
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Company Dialog */}
      <Dialog open={companyDialog} onOpenChange={(open) => { setCompanyDialog(open); if (!open) resetCompanyForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Bedrijf bewerken' : 'Nieuw bedrijf toevoegen'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCompany} className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bedrijfsnaam *</Label>
                <Input
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                  placeholder="Restaurant De Concurrent"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Categorie</Label>
                <Select value={companyForm.category} onValueChange={(v) => setCompanyForm({ ...companyForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="concurrent">Concurrent</SelectItem>
                    <SelectItem value="inspiratie">Inspiratie</SelectItem>
                    <SelectItem value="leverancier">Leverancier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Beschrijving</Label>
              <Textarea
                value={companyForm.description}
                onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })}
                placeholder="Korte beschrijving van het bedrijf..."
                rows={2}
              />
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Links & Social Media
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Globe className="w-4 h-4" /> Website</Label>
                  <Input
                    value={companyForm.website}
                    onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                    placeholder="https://www.voorbeeld.nl"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Instagram className="w-4 h-4" /> Instagram</Label>
                  <Input
                    value={companyForm.instagram}
                    onChange={(e) => setCompanyForm({ ...companyForm, instagram: e.target.value })}
                    placeholder="https://instagram.com/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Facebook className="w-4 h-4" /> Facebook</Label>
                  <Input
                    value={companyForm.facebook}
                    onChange={(e) => setCompanyForm({ ...companyForm, facebook: e.target.value })}
                    placeholder="https://facebook.com/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Linkedin className="w-4 h-4" /> LinkedIn</Label>
                  <Input
                    value={companyForm.linkedin}
                    onChange={(e) => setCompanyForm({ ...companyForm, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Star className="w-4 h-4" /> Google Reviews</Label>
                  <Input
                    value={companyForm.google_reviews}
                    onChange={(e) => setCompanyForm({ ...companyForm, google_reviews: e.target.value })}
                    placeholder="https://g.page/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>TikTok</Label>
                  <Input
                    value={companyForm.tiktok}
                    onChange={(e) => setCompanyForm({ ...companyForm, tiktok: e.target.value })}
                    placeholder="https://tiktok.com/@..."
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notities</Label>
              <Textarea
                value={companyForm.notes}
                onChange={(e) => setCompanyForm({ ...companyForm, notes: e.target.value })}
                placeholder="Extra notities over dit bedrijf..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <DialogClose asChild>
                <Button type="button" variant="outline">Annuleren</Button>
              </DialogClose>
              <Button type="submit" className="btn-primary">
                {editingCompany ? 'Opslaan' : 'Toevoegen'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Idea Dialog */}
      <Dialog open={ideaDialog} onOpenChange={(open) => { setIdeaDialog(open); if (!open) resetIdeaForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingIdea ? 'Idee bewerken' : 'Nieuw idee toevoegen'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveIdea} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Titel *</Label>
              <Input
                value={ideaForm.title}
                onChange={(e) => setIdeaForm({ ...ideaForm, title: e.target.value })}
                placeholder="Beschrijf het idee kort..."
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Beschrijving</Label>
              <Textarea
                value={ideaForm.description}
                onChange={(e) => setIdeaForm({ ...ideaForm, description: e.target.value })}
                placeholder="Uitgebreide beschrijving van het idee..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categorie</Label>
                <Select value={ideaForm.category} onValueChange={(v) => setIdeaForm({ ...ideaForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="algemeen">Algemeen</SelectItem>
                    <SelectItem value="menu">Menu</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="interieur">Interieur</SelectItem>
                    <SelectItem value="proces">Proces</SelectItem>
                    <SelectItem value="personeel">Personeel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prioriteit</Label>
                <Select value={ideaForm.priority} onValueChange={(v) => setIdeaForm({ ...ideaForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="laag">Laag</SelectItem>
                    <SelectItem value="normaal">Normaal</SelectItem>
                    <SelectItem value="hoog">Hoog</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Bron (waar gezien?)</Label>
              <Input
                value={ideaForm.source}
                onChange={(e) => setIdeaForm({ ...ideaForm, source: e.target.value })}
                placeholder="bijv. Instagram post, Website menu pagina..."
              />
            </div>

            {editingIdea && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editingIdea.status}
                  onValueChange={(v) => handleUpdateIdeaStatus(editingIdea.id, v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nieuw">Nieuw</SelectItem>
                    <SelectItem value="in_behandeling">In behandeling</SelectItem>
                    <SelectItem value="geimplementeerd">Geïmplementeerd</SelectItem>
                    <SelectItem value="afgewezen">Afgewezen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <DialogClose asChild>
                <Button type="button" variant="outline">Annuleren</Button>
              </DialogClose>
              <Button type="submit" className="btn-primary">
                {editingIdea ? 'Opslaan' : 'Toevoegen'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Creation Dialog */}
      <Dialog open={taskDialog} onOpenChange={(open) => { setTaskDialog(open); if (!open) setSelectedIdeaForTask(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-[#C2410C]" />
              Actie aanmaken van idee
            </DialogTitle>
          </DialogHeader>
          {selectedIdeaForTask && (
            <form onSubmit={handleCreateTask} className="space-y-4 pt-4">
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="font-medium text-sm">{selectedIdeaForTask.title}</p>
                <p className="text-sm text-[#57534E] mt-1 line-clamp-2">{selectedIdeaForTask.description}</p>
              </div>

              <div className="space-y-2">
                <Label>Toewijzen aan medewerker</Label>
                <Select value={taskForm.assigned_to} onValueChange={(v) => setTaskForm({ ...taskForm, assigned_to: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecteer medewerker..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Niet toegewezen</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Deadline</Label>
                  <Input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prioriteit</Label>
                  <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="laag">Laag</SelectItem>
                      <SelectItem value="normaal">Normaal</SelectItem>
                      <SelectItem value="hoog">Hoog</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="text-sm text-[#57534E]">
                De actie wordt aangemaakt in het <strong>Doelen & Structuur</strong> module en kan daar verder worden beheerd.
              </p>

              <div className="flex justify-end gap-2 pt-4">
                <DialogClose asChild>
                  <Button type="button" variant="outline">Annuleren</Button>
                </DialogClose>
                <Button type="submit" className="btn-primary">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Actie aanmaken
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Inbox Idea Dialog */}
      <Dialog open={inboxIdeaDialog} onOpenChange={(open) => { setInboxIdeaDialog(open); if (!open) resetInboxIdeaForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingInboxIdea ? 'Idee bewerken' : 'Nieuw idee toevoegen'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveInboxIdea} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Titel *</Label>
              <Input
                value={inboxIdeaForm.title}
                onChange={(e) => setInboxIdeaForm({ ...inboxIdeaForm, title: e.target.value })}
                placeholder="Beschrijf het idee kort..."
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Beschrijving</Label>
              <Textarea
                value={inboxIdeaForm.description}
                onChange={(e) => setInboxIdeaForm({ ...inboxIdeaForm, description: e.target.value })}
                placeholder="Uitgebreide beschrijving van het idee..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categorie</Label>
                <Select value={inboxIdeaForm.category} onValueChange={(v) => setInboxIdeaForm({ ...inboxIdeaForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="algemeen">Algemeen</SelectItem>
                    <SelectItem value="menu">Menu</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="interieur">Interieur</SelectItem>
                    <SelectItem value="proces">Proces</SelectItem>
                    <SelectItem value="personeel">Personeel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prioriteit</Label>
                <Select value={inboxIdeaForm.priority} onValueChange={(v) => setInboxIdeaForm({ ...inboxIdeaForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="laag">Laag</SelectItem>
                    <SelectItem value="normaal">Normaal</SelectItem>
                    <SelectItem value="hoog">Hoog</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {editingInboxIdea && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editingInboxIdea.status}
                  onValueChange={(v) => handleUpdateInboxIdeaStatus(editingInboxIdea.id, v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nieuw">Nieuw</SelectItem>
                    <SelectItem value="in_behandeling">In behandeling</SelectItem>
                    <SelectItem value="geimplementeerd">Geïmplementeerd</SelectItem>
                    <SelectItem value="afgewezen">Afgewezen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <DialogClose asChild>
                <Button type="button" variant="outline">Annuleren</Button>
              </DialogClose>
              <Button type="submit" className="btn-primary">
                {editingInboxIdea ? 'Opslaan' : 'Toevoegen'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inbox Task Creation Dialog */}
      <Dialog open={inboxTaskDialog} onOpenChange={(open) => { setInboxTaskDialog(open); if (!open) setSelectedInboxIdeaForTask(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-[#C2410C]" />
              Actie aanmaken van idee
            </DialogTitle>
          </DialogHeader>
          {selectedInboxIdeaForTask && (
            <form onSubmit={handleConvertInboxIdeaToTask} className="space-y-4 pt-4">
              <div className="p-3 bg-cyan-50 rounded-lg">
                <p className="font-medium text-sm">{selectedInboxIdeaForTask.title}</p>
                <p className="text-sm text-[#57534E] mt-1 line-clamp-2">{selectedInboxIdeaForTask.description}</p>
              </div>

              <div className="space-y-2">
                <Label>Toewijzen aan medewerker</Label>
                <Select value={taskForm.assigned_to} onValueChange={(v) => setTaskForm({ ...taskForm, assigned_to: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecteer medewerker..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Niet toegewezen</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Deadline</Label>
                  <Input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prioriteit</Label>
                  <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="laag">Laag</SelectItem>
                      <SelectItem value="normaal">Normaal</SelectItem>
                      <SelectItem value="hoog">Hoog</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="text-sm text-[#57534E]">
                De actie wordt aangemaakt in het <strong>Doelen & Structuur</strong> module en kan daar verder worden beheerd.
              </p>

              <div className="flex justify-end gap-2 pt-4">
                <DialogClose asChild>
                  <Button type="button" variant="outline">Annuleren</Button>
                </DialogClose>
                <Button type="submit" className="btn-primary">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Actie aanmaken
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
      
      {/* AI Key Required Dialog */}
      <AIKeyRequiredDialog 
        open={showAIKeyDialog} 
        onOpenChange={setShowAIKeyDialog} 
      />
    </div>
  );
};

export default ResearchPage;
